import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { FormBodyAPI } from 'src/constants/fieldsTypes';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import { commonValidationSchemaItems } from 'src/hooks/useEditModule';
import { AppVisibilityConfigSetting } from 'src/legacy/components/app-visibility/AppVisibilityConfigSetting';
import { RemoveApp } from 'src/legacy/components/AppSetup/RemoveApp';
import RowDivider from 'src/legacy/components/RowDivider';
import { SettingsPageRow } from 'src/legacy/components/Settings';
import SettingsToggleCard from 'src/legacy/components/Settings/SettingsToggleCard';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { GraySmall } from 'src/theme/colors';
import { VisibilityScenario } from 'src/utils/app_visibility';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  helperText: {
    color: GraySmall,
  },
  iconPickerWrapper: {
    marginTop: theme.spacing(3.5),
    marginBottom: theme.spacing(3.5),
  },
  removeApp: {
    paddingTop: theme.spacing(5),
  },
}));

export const useFileSettingsForm = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const validationScheme = Yup.object().shape({
    ...commonValidationSchemaItems,
    clientFileDeleteDisabled: Yup.boolean(),
  });

  const FormRenderer: React.FC<FormBodyAPI> = ({
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
  }) => (
    <>
      <BaseTextField
        autoFocus
        fullWidth
        label={
          <div className={classes.container}>
            <BaseTypography fontType="13Medium">Name</BaseTypography>
            <BaseTypography className={classes.helperText}>
              &nbsp;- How it should appear in the sidebar
            </BaseTypography>
          </div>
        }
        variant="outlined"
        sizeVariant="medium"
        name="label"
        value={values.label}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched && touched.label && errors && errors.label)}
        helperText={touched && touched.label && errors && errors.label}
      />

      <div className={classes.iconPickerWrapper}>
        <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
        <SuggestIconPicker
          value={values.icon}
          onChange={(iconName: string) => setFieldValue('icon', iconName)}
          appName={values.label}
        />
      </div>

      <SettingsPageRow hideDivider>
        <BaseTypography fontType="18Medium">
          {t(($) => $.page_files.preferences.title)}
        </BaseTypography>
        <BaseTypography className={classes.helperText}>
          {t(($) => $.page_files.preferences.description)}
        </BaseTypography>
      </SettingsPageRow>

      <RowDivider mb={0} />

      {[
        {
          id: 'client-file-add-disabled',
          title: t(($) => $.page_files.clients_can_add_files.title),
          fieldName: 'clientFileAddDisabled',
          description: t(($) => $.page_files.clients_can_add_files.description),
        },
        {
          id: 'client-file-delete-disabled',
          title: t(($) => $.page_files.clients_can_delete_files.title),
          fieldName: 'clientFileDeleteDisabled',
          description: t(
            ($) => $.page_files.clients_can_delete_files.description,
          ),
        },
        {
          id: 'client-file-update-disabled',
          title: t(($) => $.page_files.clients_can_update_files.title),
          fieldName: 'clientFileUpdateDisabled',
          description: t(
            ($) => $.page_files.clients_can_update_files.description,
          ),
        },
        {
          id: 'client-folder-update-disabled',
          title: t(($) => $.page_files.clients_can_update_folders.title),
          fieldName: 'clientFolderUpdateDisabled',
          description: t(
            ($) => $.page_files.clients_can_update_folders.description,
          ),
        },
      ].map((option) => (
        <SettingsToggleCard
          key={option.id}
          title={option.title}
          fieldName={option.fieldName}
          description={option.description}
          invertBooleanValue
          checked={values[option.fieldName]}
        />
      ))}

      <AppVisibilityConfigSetting scenario={VisibilityScenario.EditApp} />

      <div className={classes.removeApp}>
        <RemoveApp />
      </div>
    </>
  );

  return {
    validationScheme,
    FormRenderer,
  };
};
